import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ResidentialHeader, useResidentialBasePath } from '../ResidentialHome';
import { IconLock, IconUnlock } from '../../components/Icons';

/**
 * Phase 5.D — "My Gates" — the residential customer's home screen.
 *
 * Layout choice: each gate is its own white card with three regions
 *   (1) header row — illustration + name + status pill
 *   (2) BIG orange "open" CTA — the primary action lives on its own
 *       button, not the whole card. Clearer affordance than PAL's
 *       row-is-the-button model.
 *   (3) secondary row — lock toggle (left) + gear (right)
 *
 * Tapping the open CTA on a locked gate opens a confirm dialog
 * ("release lock & open?"). The lock toggle flips the i1 lock state.
 *
 * Data source: ``/api/residential/gates`` for real residential
 * customers. Super-admin preview (``/r/preview/*``) falls back to
 * ``MOCK_GATES`` so Huri staff can QA the look without provisioning
 * a customer in the DB.
 */

const MOCK_GATES = [
  {
    id: 1,
    name: 'שער חצר',
    is_open: false,
    is_locked: false,
    movement_seconds: 8,
    last_event_at: '2026-06-02T14:37:00+03:00',
  },
  {
    id: 2,
    name: 'שער כניסה',
    is_open: false,
    is_locked: true,
    movement_seconds: 8,
    last_event_at: '2026-06-02T09:12:00+03:00',
  },
];

// Used only when the backend hasn't reported a movement window yet
// (legacy gate without product spec, or preview gate missing the field).
const DEFAULT_MOVEMENT_SECONDS = 8;

function MyGatesPage({ user }) {
  const navigate = useNavigate();
  const basePath = useResidentialBasePath();
  const isPreview = basePath !== '/r';
  const [gates, setGates] = useState(isPreview ? MOCK_GATES : null);
  const [loadError, setLoadError] = useState(null);
  const [transient, setTransient] = useState({});
  const [confirmGate, setConfirmGate] = useState(null);
  // Confirmation toast — set when the Shelly acks a command so the
  // user sees "השער נפתח" / "השער נסגר" at the moment the relay
  // actually clicked, not when the UI optimistically started moving.
  const [toast, setToast] = useState(null);

  const showToast = (text) => {
    const id = Date.now() + Math.random();
    setToast({ id, text });
    setTimeout(() => {
      setToast((current) => (current && current.id === id ? null : current));
    }, 2500);
  };

  useEffect(() => {
    if (isPreview) return;
    axios.get('/api/residential/gates')
      .then(res => setGates(res.data))
      .catch(err => setLoadError(err.response?.data?.error || 'שגיאה בטעינת השערים'));
  }, [isPreview]);

  const onProfileClick = () => navigate(`${basePath}/profile`);

  const beginOpen = (gate) => {
    // Belt-and-braces: ignore the press if this gate is already mid-
    // travel. The button itself is disabled in that case, but a stale
    // tap (or a programmatic re-entry) shouldn't be able to fire a
    // second pulse that would stop the gate halfway.
    if (transient[gate.id]) return;
    if (gate.is_locked) {
      setConfirmGate(gate);
      return;
    }
    runMotionAnimation(gate);
  };

  const runMotionAnimation = (gate) => {
    const gateId = gate.id;
    const wasOpen = !!gate.is_open;
    const direction = wasOpen ? 'closing' : 'opening';
    const successText = wasOpen ? 'השער נסגר' : 'השער נפתח';
    // Travel window calculated server-side from product speed × height.
    // While this is non-zero we keep the button disabled — same trick
    // the industrial side uses to stop a double-tap from sending a
    // second pulse and stopping the gate mid-travel.
    const movementSec = gate.movement_seconds || DEFAULT_MOVEMENT_SECONDS;
    setTransient((s) => ({ ...s, [gateId]: direction }));
    if (isPreview) {
      // No backend ack to wait for in preview mode — fire the toast
      // once the optimistic travel window ends so designers still see
      // the full feedback loop.
      setTimeout(() => showToast(successText), Math.min(movementSec, 2) * 1000);
    } else {
      axios
        .post(`/api/residential/gates/${gateId}/open`)
        .then((res) => {
          const cmdId = res.data?.command_id;
          if (cmdId) waitForAckAndToast(cmdId, successText, movementSec);
        })
        .catch(() => {});
    }
    setTimeout(() => {
      setTransient((s) => {
        const next = { ...s };
        delete next[gateId];
        return next;
      });
      // Flip the optimistic state once the travel window ends. Real
      // open/closed truth will come from S1/S2 sensor events later —
      // until then this keeps the UI honest about which direction
      // the next press will move the gate.
      setGates((gs) =>
        gs.map((g) => (g.id === gateId ? { ...g, is_open: !wasOpen } : g)),
      );
    }, movementSec * 1000);
  };

  const waitForAckAndToast = (commandId, successText, movementSec) => {
    // Poll the command-status endpoint until the Shelly RPC ack arrives
    // (status === 'acknowledged'). That's our proof the relay actually
    // clicked — distinct from the limit-switch state which is only
    // reliable for "closed", not for the open direction. We give up
    // after the travel window plus a small buffer; a missing ack at
    // that point most likely means the device is offline.
    const deadline = Date.now() + (movementSec + 4) * 1000;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      axios
        .get(`/api/residential/commands/${commandId}`)
        .then((res) => {
          if (cancelled) return;
          const status = res.data?.status;
          if (status === 'acknowledged') {
            cancelled = true;
            showToast(successText);
            return;
          }
          if (status === 'failed' || status === 'timeout') {
            cancelled = true;
            return;
          }
          if (Date.now() > deadline) {
            cancelled = true;
            return;
          }
          setTimeout(tick, 600);
        })
        .catch(() => {
          if (cancelled) return;
          if (Date.now() > deadline) {
            cancelled = true;
            return;
          }
          setTimeout(tick, 600);
        });
    };
    tick();
  };

  const confirmReleaseAndOpen = () => {
    if (!confirmGate) return;
    const gate = confirmGate;
    if (!isPreview) {
      axios.post(`/api/residential/gates/${gate.id}/lock`, { locked: false }).catch(() => {});
    }
    setGates((gs) =>
      gs.map((g) => (g.id === gate.id ? { ...g, is_locked: false } : g)),
    );
    setConfirmGate(null);
    runMotionAnimation({ ...gate, is_locked: false });
  };

  const toggleLock = (gateId) => {
    setGates((gs) => {
      const next = gs.map((g) =>
        g.id === gateId ? { ...g, is_locked: !g.is_locked } : g,
      );
      const updated = next.find((g) => g.id === gateId);
      if (!isPreview && updated) {
        axios
          .post(`/api/residential/gates/${gateId}/lock`, { locked: updated.is_locked })
          .catch(() => {});
      }
      return next;
    });
  };

  const goToSettings = (gateId) => {
    navigate(`${basePath}/gates/${gateId}/settings`);
  };

  if (gates === null && !loadError) {
    return (
      <>
        <ResidentialHeader title="השערים שלי" onProfileClick={onProfileClick} />
        <div className="r-content">
          <div className="r-empty">טוען…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <ResidentialHeader title="השערים שלי" onProfileClick={onProfileClick} />
      <div className="r-content">
        {isPreview && (
          <div className="r-banner-stub">
            תצוגה מקדימה — נתונים מדומים לבחינת העיצוב.
          </div>
        )}
        {loadError && (
          <div className="r-banner-stub" style={{ background: '#FEE2E2', borderColor: '#FCA5A5', color: '#991B1B' }}>
            {loadError}
          </div>
        )}

        {!gates || gates.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="r-gate-list">
            {gates.map((gate) => (
              <GateCard
                key={gate.id}
                gate={gate}
                transient={transient[gate.id]}
                onOpen={() => beginOpen(gate)}
                onToggleLock={() => toggleLock(gate.id)}
                onSettings={() => goToSettings(gate.id)}
              />
            ))}
          </div>
        )}
      </div>

      {confirmGate && (
        <LockedConfirmDialog
          gateName={confirmGate.name}
          onConfirm={confirmReleaseAndOpen}
          onCancel={() => setConfirmGate(null)}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '1.75rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#0F172A',
            color: '#fff',
            padding: '0.7rem 1.4rem',
            borderRadius: '999px',
            fontSize: '0.95rem',
            fontWeight: 600,
            zIndex: 200,
            boxShadow: '0 10px 30px rgba(15,23,42,0.35)',
            pointerEvents: 'none',
          }}
        >
          {toast.text}
        </div>
      )}
    </>
  );
}

function GateCard({ gate, transient, onOpen, onToggleLock, onSettings }) {
  const isOpening = transient === 'opening';
  const isClosing = transient === 'closing';
  const inMotion = isOpening || isClosing;
  // Reuse the amber "in motion" treatment for both opening and closing
  // — same visual signal that the gate is moving and the button is
  // temporarily locked out.
  const cls =
    'r-gate-card' +
    (gate.is_locked ? ' is-locked' : '') +
    (inMotion ? ' is-opening' : '');
  const actionLabel = gate.is_locked
    ? 'השער נעול'
    : isOpening
      ? 'פותח…'
      : isClosing
        ? 'סוגר…'
        : gate.is_open
          ? 'סגור שער'
          : 'פתח שער';
  const statusLabel = isOpening
    ? 'פותח…'
    : isClosing
      ? 'סוגר…'
      : gate.is_open
        ? 'פתוח'
        : 'סגור';
  return (
    <div className={cls}>
      <div className="r-gate-card-top">
        <div className="r-gate-illo" aria-hidden="true">
          <ShutterGateIcon />
        </div>
        <div className="r-gate-text">
          <div className="r-gate-name">{gate.name}</div>
          {gate.address && (
            <div style={{
              fontSize: '0.78rem',
              color: '#64748b',
              marginTop: '-0.1rem',
              marginBottom: '0.2rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>{gate.address}</div>
          )}
          <span
            className={
              'r-gate-status-pill' +
              (inMotion ? ' is-opening' : gate.is_open ? ' is-open' : '')
            }
          >
            <span className="dot" aria-hidden="true" />
            {statusLabel}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={
          'r-open-btn' +
          (inMotion ? ' is-opening' : '') +
          (gate.is_locked ? ' is-locked-look' : '')
        }
        onClick={onOpen}
        disabled={inMotion}
        aria-label={`${actionLabel} – ${gate.name}`}
      >
        {gate.is_locked ? (
          <>
            <IconLock size={18} />
            <span>השער נעול</span>
          </>
        ) : (
          <span>{actionLabel}</span>
        )}
      </button>

      <div className="r-gate-secondary">
        <button
          type="button"
          className={'r-lock-toggle' + (gate.is_locked ? ' is-locked' : '')}
          onClick={onToggleLock}
          aria-label={gate.is_locked ? 'שחרר נעילה' : 'נעל את השער'}
        >
          <span className="r-lock-toggle-icon">
            {gate.is_locked ? <IconLock size={14} /> : <IconUnlock size={14} />}
          </span>
          <span className="r-lock-toggle-label">
            <span>{gate.is_locked ? 'נעול' : 'לא נעול'}</span>
            <small>{gate.is_locked ? 'לחצי לשחרור' : 'לחצי לנעילה'}</small>
          </span>
        </button>
        <button
          type="button"
          className="r-gear-btn"
          onClick={onSettings}
          aria-label="הגדרות שער"
        >
          <GearIcon />
        </button>
      </div>
    </div>
  );
}

/**
 * Custom shutter / rolling-gate icon. Deliberately not the generic
 * barrier icon used in PAL — this is a closed shutter with horizontal
 * slats and a small handle, more honest to a residential install.
 */
function ShutterGateIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="6" width="26" height="24" rx="2" />
      <line x1="5" y1="11" x2="31" y2="11" />
      <line x1="5" y1="16" x2="31" y2="16" />
      <line x1="5" y1="21" x2="31" y2="21" />
      <line x1="5" y1="26" x2="31" y2="26" />
      <circle cx="18" cy="30" r="0.8" fill="currentColor" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function LockedConfirmDialog({ gateName, onConfirm, onCancel }) {
  return (
    <div className="r-modal-backdrop" role="dialog" aria-modal="true">
      <div className="r-modal">
        <div className="r-modal-icon" aria-hidden="true">
          <IconLock size={28} />
        </div>
        <h3>השער נעול</h3>
        <p>
          {gateName} נעול כרגע. לשחרר את הנעילה ולפתוח אותו?
        </p>
        <div className="r-modal-actions">
          <button type="button" className="r-btn r-btn-secondary" onClick={onCancel}>
            ביטול
          </button>
          <button type="button" className="r-btn r-btn-primary" onClick={onConfirm}>
            שחרר ופתח
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="r-empty">
      <strong>עדיין אין שערים מוגדרים.</strong>
      <br />
      הטכנאי של Huri יוסיף את השערים שלך בהתקנה.
    </div>
  );
}

export default MyGatesPage;
